import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Tabs,
  Tab,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Menu,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  ArrowBack as BackIcon,
  Storage as SeedIcon,
} from '@mui/icons-material';
import {
  fetchRuleSets,
  fetchRuleSet,
  createRuleSet,
  createRule,
  updateRule,
  deleteRule,
  importRules,
  seedDefaultRules,
  clearCurrentRuleSet,
} from '../redux/slices/networkRulesSlice';
import networkRulesService from '../services/api/networkRulesService';
import appRegistryService from '../services/api/appRegistryService';

const RULE_TYPES = [
  { value: 'allow', label: 'Allow' },
  { value: 'block', label: 'Block' },
];

const RULE_TARGETS = [
  { value: 'domain', label: 'Domain' },
  { value: 'url_pattern', label: 'URL Pattern' },
  { value: 'regex', label: 'Regex' },
];

// --- Rule Set List View ---
function RuleSetListView({ ruleSets, loading, onSelect, onCreateSet, onSeed }) {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Network Rule Sets</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<SeedIcon />}
            onClick={onSeed}
            sx={{ mr: 1 }}
          >
            Seed Defaults
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateSet}>
            New Rule Set
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Client Tag</TableCell>
                <TableCell align="center">Rules</TableCell>
                <TableCell align="center">Default</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ruleSets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No rule sets yet. Create one or seed defaults.
                  </TableCell>
                </TableRow>
              ) : (
                ruleSets.map((set) => (
                  <TableRow
                    key={set.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onSelect(set.id)}
                  >
                    <TableCell>
                      <Typography fontWeight="bold">{set.name}</Typography>
                      {set.description && (
                        <Typography variant="body2" color="text.secondary">
                          {set.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {set.clientTag ? (
                        <Chip label={set.clientTag} size="small" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">--</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={set.ruleCount ?? '...'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      {set.isDefault ? <Chip label="Default" color="primary" size="small" /> : '--'}
                    </TableCell>
                    <TableCell>
                      {set.createdAt ? new Date(set.createdAt).toLocaleDateString() : '--'}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={(e) => { e.stopPropagation(); onSelect(set.id); }}>
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// --- Rule Detail View (rules within a set) ---
function RuleSetDetailView({
  ruleSet,
  rules,
  loading,
  onBack,
  onAddRule,
  onEditRule,
  onDeleteRule,
  onExport,
  onImport,
  searchTerm,
  onSearchChange,
  filterType,
  onFilterTypeChange,
  apps,
}) {
  const [exportAnchor, setExportAnchor] = useState(null);

  if (!ruleSet) return null;

  const filteredRules = rules.filter((r) => {
    const matchesSearch =
      !searchTerm ||
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.pattern?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || r.ruleType === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={1}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h5">{ruleSet.name}</Typography>
          {ruleSet.description && (
            <Typography variant="body2" color="text.secondary">{ruleSet.description}</Typography>
          )}
        </Box>
        <Button variant="outlined" startIcon={<ImportIcon />} onClick={onImport} sx={{ mr: 1 }}>
          Import
        </Button>
        <Button
          variant="outlined"
          startIcon={<ExportIcon />}
          onClick={(e) => setExportAnchor(e.currentTarget)}
          sx={{ mr: 1 }}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={() => setExportAnchor(null)}
        >
          <MenuItem onClick={() => { onExport('extension'); setExportAnchor(null); }}>
            Extension Blocker (JSON)
          </MenuItem>
          <MenuItem onClick={() => { onExport('squid'); setExportAnchor(null); }}>
            Squid ACL Config
          </MenuItem>
          <MenuItem onClick={() => { onExport('pac'); setExportAnchor(null); }}>
            PAC File
          </MenuItem>
        </Menu>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onAddRule}>
          Add Rule
        </Button>
      </Box>

      <Toolbar disableGutters sx={{ gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search rules..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Rule Type</InputLabel>
          <Select
            value={filterType}
            label="Rule Type"
            onChange={(e) => onFilterTypeChange(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="allow">Allow</MenuItem>
            <MenuItem value="block">Block</MenuItem>
          </Select>
        </FormControl>
      </Toolbar>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Application</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Pattern</TableCell>
                <TableCell align="center">Priority</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No rules found. Add a rule or import from a file.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{rule.name}</Typography>
                      {rule.description && (
                        <Typography variant="caption" color="text.secondary">
                          {rule.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {rule.appDefinitionId ? (
                        <Chip
                          label={apps.find((a) => a.id === rule.appDefinitionId)?.name || rule.appDefinitionId}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="Global" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.ruleType}
                        color={rule.ruleType === 'allow' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={rule.target} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{rule.pattern}</Typography>
                    </TableCell>
                    <TableCell align="center">{rule.priority}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={rule.isActive ? 'Active' : 'Inactive'}
                        color={rule.isActive ? 'primary' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onEditRule(rule)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => onDeleteRule(rule.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// --- Rule Dialog (Create/Edit) ---
function RuleDialog({ open, onClose, onSave, rule, ruleSetId, apps }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    appDefinitionId: '',
    ruleType: 'block',
    target: 'domain',
    pattern: '',
    priority: 0,
    isActive: true,
  });

  useEffect(() => {
    if (rule) {
      setForm({
        name: rule.name || '',
        description: rule.description || '',
        appDefinitionId: rule.appDefinitionId || '',
        ruleType: rule.ruleType || 'block',
        target: rule.target || 'domain',
        pattern: rule.pattern || '',
        priority: rule.priority ?? 0,
        isActive: rule.isActive !== false,
      });
    } else {
      setForm({ name: '', description: '', appDefinitionId: '', ruleType: 'block', target: 'domain', pattern: '', priority: 0, isActive: true });
    }
  }, [rule, open]);

  const handleSave = () => {
    const data = { ...form, ruleSetId };
    // Send null instead of empty string so the DB stores NULL for global rules
    if (!data.appDefinitionId) data.appDefinitionId = null;
    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rule ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel>Application (optional)</InputLabel>
            <Select
              value={form.appDefinitionId}
              label="Application (optional)"
              onChange={(e) => setForm({ ...form, appDefinitionId: e.target.value })}
            >
              <MenuItem value="">Global (all apps)</MenuItem>
              {(apps || []).map((app) => (
                <MenuItem key={app.id} value={app.id}>{app.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Rule Type</InputLabel>
              <Select
                value={form.ruleType}
                label="Rule Type"
                onChange={(e) => setForm({ ...form, ruleType: e.target.value })}
              >
                {RULE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Target</InputLabel>
              <Select
                value={form.target}
                label="Target"
                onChange={(e) => setForm({ ...form, target: e.target.value })}
              >
                {RULE_TARGETS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Pattern"
            value={form.pattern}
            onChange={(e) => setForm({ ...form, pattern: e.target.value })}
            fullWidth
            required
            helperText={
              form.target === 'domain'
                ? 'e.g. example.com'
                : form.target === 'url_pattern'
                ? 'e.g. *://*.example.com/*'
                : 'e.g. ^https?://.*\\.example\\.com'
            }
          />
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label="Priority"
              type="number"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
              sx={{ width: 120 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!form.name || !form.pattern}
        >
          {rule ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Create Rule Set Dialog ---
function CreateRuleSetDialog({ open, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', description: '', clientTag: '' });

  useEffect(() => {
    if (open) setForm({ name: '', description: '', clientTag: '' });
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Rule Set</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Client Tag"
            value={form.clientTag}
            onChange={(e) => setForm({ ...form, clientTag: e.target.value })}
            fullWidth
            helperText="Optional: tag for client-specific rules"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} disabled={!form.name}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Import Dialog ---
function ImportDialog({ open, onClose, onImport, ruleSetId }) {
  const [format, setFormat] = useState('extension_filters');
  const [data, setData] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setData(''); setError(''); setFormat('extension_filters'); }
  }, [open]);

  const handleImport = () => {
    try {
      const parsed = JSON.parse(data);
      onImport({ format, data: parsed, ruleSetId });
    } catch (e) {
      setError('Invalid JSON data');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Rules</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <FormControl fullWidth>
            <InputLabel>Format</InputLabel>
            <Select value={format} label="Format" onChange={(e) => setFormat(e.target.value)}>
              <MenuItem value="extension_filters">Extension Filters (defaultFilters.json)</MenuItem>
              <MenuItem value="content_filters_table">Content Filters Table</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="JSON Data"
            value={data}
            onChange={(e) => { setData(e.target.value); setError(''); }}
            fullWidth
            multiline
            rows={10}
            placeholder={
              format === 'extension_filters'
                ? '{\n  "URLMatchPatterns": ["*://*.example.com/*"],\n  "URLDenyPatterns": [],\n  "HostSearchList": ["example.com"]\n}'
                : '[{"name": "Example", "domain": "example.com", "filterType": "allow"}]'
            }
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleImport} disabled={!data}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Main Page Component ---
export default function NetworkRules() {
  const dispatch = useDispatch();
  const { ruleSets, currentRuleSet, rules, loading, error } = useSelector(
    (state) => state.networkRules
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [createSetOpen, setCreateSetOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [apps, setApps] = useState([]);

  useEffect(() => {
    dispatch(fetchRuleSets());
    // Load available apps for the application dropdown
    appRegistryService.getApps()
      .then((res) => setApps(res.data || []))
      .catch(() => setApps([]));
  }, [dispatch]);

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSelectSet = useCallback((id) => {
    dispatch(fetchRuleSet(id));
  }, [dispatch]);

  const handleBack = useCallback(() => {
    dispatch(clearCurrentRuleSet());
    dispatch(fetchRuleSets());
  }, [dispatch]);

  const handleCreateSet = useCallback(async (data) => {
    await dispatch(createRuleSet(data));
    setCreateSetOpen(false);
    showMessage('Rule set created');
  }, [dispatch, showMessage]);

  const handleSaveRule = useCallback(async (data) => {
    if (editingRule) {
      await dispatch(updateRule({ id: editingRule.id, data }));
      showMessage('Rule updated');
    } else {
      await dispatch(createRule(data));
      showMessage('Rule created');
    }
    setRuleDialogOpen(false);
    setEditingRule(null);
    // Refresh the current rule set
    if (currentRuleSet) dispatch(fetchRuleSet(currentRuleSet.id));
  }, [dispatch, editingRule, currentRuleSet, showMessage]);

  const handleDeleteRule = useCallback(async (id) => {
    if (window.confirm('Delete this rule?')) {
      await dispatch(deleteRule(id));
      showMessage('Rule deleted');
    }
  }, [dispatch, showMessage]);

  const handleExport = useCallback(async (format) => {
    if (!currentRuleSet) return;
    try {
      let content, filename, type;
      if (format === 'extension') {
        content = JSON.stringify(await networkRulesService.exportExtension(currentRuleSet.id), null, 2);
        filename = `${currentRuleSet.name}-extension.json`;
        type = 'application/json';
      } else if (format === 'squid') {
        content = await networkRulesService.exportSquid(currentRuleSet.id);
        filename = `${currentRuleSet.name}-squid.conf`;
        type = 'text/plain';
      } else if (format === 'pac') {
        content = await networkRulesService.exportPac(currentRuleSet.id);
        filename = `${currentRuleSet.name}-proxy.pac`;
        type = 'application/x-ns-proxy-autoconfig';
      }
      const blob = new Blob([content], { type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      showMessage(`Exported as ${format}`);
    } catch (err) {
      showMessage(`Export failed: ${err.message}`, 'error');
    }
  }, [currentRuleSet, showMessage]);

  const handleImport = useCallback(async (data) => {
    try {
      await dispatch(importRules(data));
      setImportOpen(false);
      showMessage('Rules imported successfully');
      if (currentRuleSet) dispatch(fetchRuleSet(currentRuleSet.id));
    } catch (err) {
      showMessage(`Import failed: ${err.message}`, 'error');
    }
  }, [dispatch, currentRuleSet, showMessage]);

  const handleSeed = useCallback(async () => {
    await dispatch(seedDefaultRules());
    dispatch(fetchRuleSets());
    showMessage('Default rules seeded');
  }, [dispatch, showMessage]);

  return (
    <Box p={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {currentRuleSet ? (
        <>
          <RuleSetDetailView
            ruleSet={currentRuleSet}
            rules={rules}
            loading={loading}
            onBack={handleBack}
            onAddRule={() => { setEditingRule(null); setRuleDialogOpen(true); }}
            onEditRule={(rule) => { setEditingRule(rule); setRuleDialogOpen(true); }}
            onDeleteRule={handleDeleteRule}
            onExport={handleExport}
            onImport={() => setImportOpen(true)}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterType={filterType}
            onFilterTypeChange={setFilterType}
            apps={apps}
          />
          <RuleDialog
            open={ruleDialogOpen}
            onClose={() => { setRuleDialogOpen(false); setEditingRule(null); }}
            onSave={handleSaveRule}
            rule={editingRule}
            ruleSetId={currentRuleSet.id}
            apps={apps}
          />
          <ImportDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onImport={handleImport}
            ruleSetId={currentRuleSet.id}
          />
        </>
      ) : (
        <>
          <RuleSetListView
            ruleSets={ruleSets}
            loading={loading}
            onSelect={handleSelectSet}
            onCreateSet={() => setCreateSetOpen(true)}
            onSeed={handleSeed}
          />
          <CreateRuleSetDialog
            open={createSetOpen}
            onClose={() => setCreateSetOpen(false)}
            onSave={handleCreateSet}
          />
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
